import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, type ConversationSummary } from "../lib/api";
import { getLastChatId, setLastChatId } from "../lib/lastChat";

function when(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const days = Math.floor((now.getTime() - ts) / 86_400_000);
  if (days < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function HistoryIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </svg>
  );
}

export function HistoryDrawer({
  open,
  activeId,
  onClose,
  onPick,
}: {
  open: boolean;
  activeId?: string;
  onClose: () => void;
  onPick: (id: string) => void;
}) {
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ConversationSummary[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api
      .conversations()
      .then(({ conversations }) => {
        if (cancelled) return;
        setRows(conversations);
        setError("");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.title.toLowerCase().includes(q) || r.preview.toLowerCase().includes(q),
    );
  }, [rows, query]);

  const pick = (id: string) => {
    onPick(id);
    onClose();
  };

  const remove = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm("Delete this conversation?")) return;
    try {
      await api.deleteConversation(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      if (id === getLastChatId()) setLastChatId(null);
      if (id === activeId) {
        navigate("/chat");
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className={`drawer-root ${open ? "is-open" : ""}`} aria-hidden={!open}>
      <div className="drawer__backdrop" onClick={onClose} />
      <aside
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Conversation history"
        inert={!open}
      >
        <header className="drawer__head">
          <h2 className="drawer__title">History</h2>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              navigate("/chat");
              onClose();
            }}
          >
            New chat
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--icon"
            onClick={onClose}
            aria-label="Close history"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        <div className="drawer__search">
          <input
            ref={searchRef}
            className="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search conversations"
            type="search"
            autoCapitalize="none"
            autoCorrect="off"
            tabIndex={open ? 0 : -1}
          />
        </div>

        <div className="drawer__body scroll">
          {error && <div className="banner banner--fault">{error}</div>}
          {loading && !rows.length && <div className="empty">Loading…</div>}
          {!loading && !filtered.length && (
            <div className="empty">{query ? "Nothing matched." : "No conversations yet."}</div>
          )}

          {filtered.length > 0 && (
            <div className="list">
              {filtered.map((c) => {
                const current = c.id === activeId;
                return (
                  <div key={c.id} className={`list__row${current ? " is-current" : ""}`}>
                    <button
                      type="button"
                      className="list__rowMain"
                      onClick={() => pick(c.id)}
                      aria-current={current ? "true" : undefined}
                    >
                      <div className="grow">
                        <div className="list__title truncate">{c.title || "Untitled"}</div>
                        <div className="list__sub truncate">{c.preview || "—"}</div>
                      </div>
                      <div className="list__meta">
                        <span className="pill">{c.engine}</span>
                        <span className="list__time">{when(c.updatedAt)}</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      className="list__del"
                      aria-label="Delete conversation"
                      onClick={(e) => void remove(c.id, e)}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
